/**
 * RejectedList — Dev mode → Knowledge → Rejected (SUN-89). Admin only (the screen and the server route
 * both need kiaan_manage_knowledge). Files the automatic check refused before storing them: text written
 * to an AI, a personal ID or bank number, a price or size that does not match the catalogue, off-topic,
 * or the check could not run. The text itself was never stored, so nothing here is searchable.
 */
import './knowledge.css';
import { useEffect, useState } from 'react';
import { knowledgeLoadErrorText, listRejected, type RejectedItem } from './knowledgeApi';
import { whenText } from './whenText';

export const REASON_LABEL: Record<string, string> = {
  injection: 'Text written to an AI',
  personal_id: 'Personal ID or bank number',
  conflict: 'Does not match the catalogue',
  off_topic: 'Not about the company',
  not_checked: 'Check could not run',
};

export function RejectedList() {
  const [rows, setRows] = useState<RejectedItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    listRejected()
      .then((r) => { if (alive) setRows(r); })
      .catch((e) => { if (alive) setError(knowledgeLoadErrorText(e)); });
    return () => { alive = false; };
  }, []);

  if (error) return <div className="kw-banner" role="status">{error}</div>;
  if (!rows) return <div className="kw-small" style={{ padding: 12 }}>Loading…</div>;
  return (
    <div className="kn-pane">
      <div className="kw-small">Files the automatic check refused. They were not saved, so no agent can find them. Fix the file and upload it again.</div>
      {rows.length === 0 ? (
        <div className="kw-small" role="status">Nothing has been refused.</div>
      ) : (
        <table className="kn-table" aria-label="Rejected files">
          <thead><tr><th>When</th><th>File</th><th>Where</th><th>Why</th></tr></thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={`${r.at}-${i}`}>
                <td>{whenText(r.at)}</td>
                <td>{r.file || '—'}</td>
                <td>{r.container || '—'}</td>
                <td>
                  <strong>{r.reasons.map((x) => REASON_LABEL[x] ?? x).join(', ')}</strong>
                  {r.notes.length > 0 && <div className="kw-small">{r.notes.join(' ')}</div>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
